import { useEffect } from "react";
import { zodResolver } from "@hookform/resolvers/zod";
import {
    createStyles,
    Grid,
    TextField,
    Theme,
    Typography,
    withStyles,
    WithStyles
} from "@material-ui/core";
import { Add, Remove, Save } from "@material-ui/icons";
import { useSnackbar } from "notistack";
import { useFieldArray, useForm, useWatch } from "react-hook-form";
import * as z from "zod";
import {
    useCreateParcelLocationMutation,
    useDeleteParcelLocationMutation,
    useGetParcelLocationsQuery,
    useUpdateParcelLocationMutation
} from "../../../api/parcelApiSlice";
import LoaderButton from "../LoaderButton";

const styles = (theme: Theme) =>
    createStyles({
        addRowButton: {
            backgroundColor: theme.palette.success.main,
            "&:hover": {
                backgroundColor: theme.palette.success.main
            }
        },
        deleteRowButton: {
            backgroundColor: theme.palette.error.main,
            "&:hover": {
                backgroundColor: theme.palette.error.main
            }
        }
    });

const schema = z.object({
    parcelLocations: z.array(
        z.object({
            parcelLocationId: z.string().optional(),
            latitude: z.preprocess(
                (val) =>
                    typeof val === "number" && isNaN(val) ? undefined : val,
                z
                    .number()
                    .min(-90)
                    .max(90, "Latitude must be between -90 and 90.")
                    .optional()
            ),
            legalLandDescription: z.string(),
            longitude: z.preprocess(
                (val) =>
                    typeof val === "number" && isNaN(val) ? undefined : val,
                z
                    .number()
                    .min(-180)
                    .max(180, "Longitude must be between -180 and 180.")
                    .optional()
            ),
            acres: z.number().positive("Acres must be a positive number")
        })
    )
});

interface IParcelLocationFormValues extends z.infer<typeof schema> {}

const DEFAULT_PARCEL_LOCATION = { legalLandDescription: "NW-", acres: 0 };

interface IParcelLocationForm extends WithStyles<typeof styles> {
    parcelId: string;
    isDisabled?: boolean;
}

function ParcelLocationForm({
    classes,
    parcelId,
    isDisabled
}: IParcelLocationForm) {
    const {
        control,
        register,
        setValue,
        setError,
        formState: { errors }
    } = useForm<IParcelLocationFormValues>({
        defaultValues: {
            parcelLocations: []
        },
        resolver: zodResolver(schema)
    });

    const { fields, append, remove } = useFieldArray({
        control,
        name: "parcelLocations"
    });

    const { enqueueSnackbar } = useSnackbar();

    const formValues = useWatch({
        name: "parcelLocations",
        control
    });

    const parcelLocationsQuery = useGetParcelLocationsQuery(parcelId);
    const [createParcelLocationMutation, createParcelLocationMutationData] =
        useCreateParcelLocationMutation();
    const [updateParcelLocationMutation, updateParcelLocationMutationData] =
        useUpdateParcelLocationMutation();
    const [deleteParcelLocationMutation, deleteParcelLocationMutationData] =
        useDeleteParcelLocationMutation();

    const isLoading =
        parcelLocationsQuery.isLoading ||
        createParcelLocationMutationData.isLoading ||
        updateParcelLocationMutationData.isLoading ||
        deleteParcelLocationMutationData.isLoading;

    useEffect(() => {
        if (parcelLocationsQuery.data) {
            setValue("parcelLocations", parcelLocationsQuery.data);
        }
    }, [parcelLocationsQuery.data, setValue]);

    const resetFormErrors = () => setError("parcelLocations", { message: "" });

    const addRow = () => {
        resetFormErrors();
        append(DEFAULT_PARCEL_LOCATION);
    };

    const deleteRow = () => {
        resetFormErrors();
        if (formValues.length) {
            remove(formValues.length - 1);
        }
    };

    const onSubmit = async () => {
        try {
            resetFormErrors();

            await schema.parseAsync({ parcelLocations: formValues });

            // NOTE: We are intentionally executing these promises sequentially to persist order

            const existingParcelLocations = parcelLocationsQuery.data || [];
            const currentIds = new Set(
                formValues.map(
                    (parcelLocation) => parcelLocation.parcelLocationId
                )
            );

            for (const parcelLocationFormValue of formValues) {
                if (parcelLocationFormValue.parcelLocationId) {
                    const existingParcelLocation = existingParcelLocations.find(
                        (p) =>
                            p.parcelLocationId ===
                            parcelLocationFormValue.parcelLocationId
                    );
                    const hasAcresChanged =
                        existingParcelLocation?.acres !==
                        parcelLocationFormValue.acres;
                    const hasLatitudeChanged =
                        existingParcelLocation?.latitude !==
                        parcelLocationFormValue.latitude;
                    const hasLegalLandDescriptionChanged =
                        existingParcelLocation?.legalLandDescription !==
                        parcelLocationFormValue.legalLandDescription;
                    const hasLongitudeChanged =
                        existingParcelLocation?.longitude !==
                        parcelLocationFormValue.longitude;

                    const hasChanges =
                        hasAcresChanged ||
                        hasLatitudeChanged ||
                        hasLongitudeChanged ||
                        hasLegalLandDescriptionChanged;

                    if (hasChanges) {
                        await updateParcelLocationMutation({
                            parcelId,
                            parcelLocationId:
                                parcelLocationFormValue.parcelLocationId,
                            parcelLocation: {
                                acres: parcelLocationFormValue.acres,
                                latitude: parcelLocationFormValue.latitude,
                                legalLandDescription:
                                    parcelLocationFormValue.legalLandDescription,
                                longitude: parcelLocationFormValue.longitude
                            }
                        }).unwrap();
                    }
                } else {
                    await createParcelLocationMutation({
                        parcelId,
                        parcelLocation: {
                            acres: parcelLocationFormValue.acres,
                            latitude: parcelLocationFormValue.latitude,
                            legalLandDescription:
                                parcelLocationFormValue.legalLandDescription,
                            longitude: parcelLocationFormValue.longitude
                        }
                    }).unwrap();
                }
            }

            for (const parcelLocation of existingParcelLocations) {
                if (
                    parcelLocation.parcelLocationId &&
                    !currentIds.has(parcelLocation.parcelLocationId)
                ) {
                    await deleteParcelLocationMutation({
                        parcelId: parcelId,
                        parcelLocationId: parcelLocation.parcelLocationId
                    }).unwrap();
                }
            }

            enqueueSnackbar("Parcel location successfully saved", {
                anchorOrigin: { horizontal: "left", vertical: "top" },
                preventDuplicate: false,
                variant: "success"
            });
        } catch (e) {
            console.error(e);

            enqueueSnackbar("Parcel location failed to save", {
                anchorOrigin: { horizontal: "left", vertical: "top" },
                preventDuplicate: false,
                variant: "error"
            });

            if (e instanceof z.ZodError) {
                return setError("parcelLocations", {
                    message: e.errors[0].message
                });
            }
            if (e instanceof Error) {
                return setError("parcelLocations", {
                    message: (e as Error).message
                });
            }
            return setError("parcelLocations", {
                message: "Unknown error occurred while saving"
            });
        }
    };

    return (
        <>
            <Grid container spacing={4}>
                {fields.map((field, index) => (
                    <Grid item xs={12} key={`${field.id}-grid`}>
                        <Grid container spacing={4}>
                            <Grid item xs={12} sm={6}>
                                <TextField
                                    disabled={isDisabled}
                                    margin="normal"
                                    fullWidth
                                    label={`LSD ${index + 1}`}
                                    type="text"
                                    key={`${field.id}-legalLandDescription`}
                                    placeholder="NW-0-0-0-W5"
                                    {...register(
                                        `parcelLocations.${index}.legalLandDescription`,
                                        {
                                            required: true
                                        }
                                    )}
                                />
                            </Grid>
                            <Grid item xs={12} sm={6}>
                                <TextField
                                    disabled={isDisabled}
                                    margin="normal"
                                    fullWidth
                                    label={`Acres ${index + 1}`}
                                    type="number"
                                    key={`${field.id}-acres`}
                                    placeholder="160"
                                    {...register(
                                        `parcelLocations.${index}.acres`,
                                        {
                                            required: true,
                                            valueAsNumber: true
                                        }
                                    )}
                                />
                            </Grid>
                        </Grid>
                        <Grid container spacing={4}>
                            <Grid item xs={12} sm={6}>
                                <TextField
                                    disabled={isDisabled}
                                    margin="normal"
                                    fullWidth
                                    label={`Latitude ${index + 1}`}
                                    type="number"
                                    key={`${field.id}-latitude`}
                                    placeholder="55.1714"
                                    {...register(
                                        `parcelLocations.${index}.latitude`,
                                        {
                                            required: false,
                                            valueAsNumber: true
                                        }
                                    )}
                                />
                            </Grid>
                            <Grid item xs={12} sm={6}>
                                <TextField
                                    disabled={isDisabled}
                                    margin="normal"
                                    fullWidth
                                    label={`Longitude ${index + 1}`}
                                    type="number"
                                    key={`${field.id}-longitude`}
                                    placeholder="-118.7888"
                                    {...register(
                                        `parcelLocations.${index}.longitude`,
                                        {
                                            required: false,
                                            valueAsNumber: true
                                        }
                                    )}
                                />
                            </Grid>
                        </Grid>
                    </Grid>
                ))}

                {errors.parcelLocations?.message && (
                    <Grid item xs={12}>
                        <Typography variant="body2" color="error">
                            {errors.parcelLocations.message}
                        </Typography>
                    </Grid>
                )}

                {!formValues.length && (
                    <Grid item xs={12}>
                        <Typography variant="body2" color="textSecondary">
                            No legal land locations.
                        </Typography>
                    </Grid>
                )}

                <Grid item xs={12}>
                    <Grid container spacing={2}>
                        <Grid item>
                            <LoaderButton
                                loading={isLoading}
                                type="button"
                                variant="contained"
                                color="primary"
                                onClick={onSubmit}
                                disabled={isDisabled}
                            >
                                <Save />
                                &nbsp; Update
                            </LoaderButton>
                        </Grid>

                        <Grid item>
                            <LoaderButton
                                disabled={isLoading || isDisabled}
                                type="button"
                                variant="contained"
                                color="secondary"
                                className={classes.addRowButton}
                                onClick={addRow}
                            >
                                <Add />
                                &nbsp; Add Row
                            </LoaderButton>
                        </Grid>

                        <Grid item>
                            <LoaderButton
                                disabled={
                                    isLoading ||
                                    isDisabled ||
                                    !formValues.length
                                }
                                type="button"
                                color="primary"
                                variant="contained"
                                className={classes.deleteRowButton}
                                onClick={deleteRow}
                            >
                                <Remove />
                                &nbsp; Delete Row
                            </LoaderButton>
                        </Grid>
                    </Grid>
                </Grid>
            </Grid>
        </>
    );
}

export default withStyles(styles)(ParcelLocationForm);
